

import { useState } from "react"
import {
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  CreditCard,
  Shield,
  Clock,
  CheckCircle,
  Gift,
  Percent,
  ArrowRight,
  X,
} from "lucide-react"


// Composants UI de base
const Button = ({ children, variant = "default", className = "", onClick, disabled, ...props }) => {
  const baseClasses =
    "inline-flex items-center justify-center rounded-md text-sm font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none"
  const variants = {
    default: "bg-blue-600 text-white hover:bg-blue-700 h-10 px-4 py-2",
    outline: "border border-gray-300 bg-white hover:bg-gray-50 h-10 px-4 py-2",
    ghost: "hover:bg-gray-100 text-gray-700 h-10 px-4 py-2",
    destructive: "bg-red-600 text-white hover:bg-red-700 h-10 px-4 py-2",
    success: "bg-green-600 text-white hover:bg-green-700 h-10 px-4 py-2",
    primary: "bg-green-600 text-white hover:bg-green-700 h-12 px-6 py-3 text-base",
  }

  return (
    <button
      className={`${baseClasses} ${variants[variant]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  )
}

const Card = ({ children, className = "" }) => {
  return <div className={`bg-white rounded-lg border border-gray-200 shadow-sm ${className}`}>{children}</div>
}

const Badge = ({ children, variant = "default", className = "" }) => {
  const baseClasses = "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
  const variants = {
    default: "bg-blue-100 text-blue-800",
    success: "bg-green-100 text-green-800",
    warning: "bg-yellow-100 text-yellow-800",
    danger: "bg-red-100 text-red-800",
    secondary: "bg-gray-100 text-gray-800",
  }

  return <span className={`${baseClasses} ${variants[variant]} ${className}`}>{children}</span>
}

const Input = ({ className = "", ...props }) => {
  return (
    <input
      className={`flex h-10 w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent ${className}`}
      {...props}
    />
  )
}

// Services disponibles
const availableServices = [
  {
    id: "consultation",
    name: "Consultation Orientation",
    description: "Session personnalisée de 1h avec un expert pour choisir votre université",
    price: 50,
    originalPrice: 75,
    category: "Conseil",
    duration: "1 heure",
    delivery: "Vidéoconférence",
    popular: true,
    features: ["Analyse de profil", "Recommandations personnalisées", "Plan d'action", "Support email 7j"],
  },
  {
    id: "visa-assistance",
    name: "Assistance Visa Étudiant",
    description: "Accompagnement complet pour votre demande de visa étudiant",
    price: 120,
    originalPrice: 150,
    category: "Visa",
    duration: "2-3 semaines",
    delivery: "Suivi personnalisé",
    features: ["Vérification documents", "Préparation dossier", "Suivi demande", "Support jusqu'à obtention"],
  },
  {
    id: "housing-finder",
    name: "Recherche de Logement",
    description: "Service de recherche et réservation de logement étudiant",
    price: 80,
    originalPrice: 100,
    category: "Logement",
    duration: "1-2 semaines",
    delivery: "Liste personnalisée",
    features: ["Sélection selon budget", "Visite virtuelle", "Négociation prix", "Contrat sécurisé"],
  },
  {
    id: "university-application",
    name: "Aide Candidature Université",
    description: "Assistance pour vos candidatures dans les universités kenyanes",
    price: 90,
    originalPrice: 120,
    category: "Candidature",
    duration: "3-4 semaines",
    delivery: "Dossiers optimisés",
    features: ["Optimisation dossier", "Lettre motivation", "Suivi candidatures", "Préparation entretiens"],
  },
  {
    id: "complete-package",
    name: "Pack Complet Étudiant",
    description: "Tous nos services pour une prise en charge complète",
    price: 280,
    originalPrice: 445,
    category: "Pack",
    duration: "1-2 mois",
    delivery: "Accompagnement total",
    popular: true,
    discount: 37,
    features: [
      "Consultation orientation",
      "Assistance visa",
      "Recherche logement",
      "Aide candidatures",
      "Support prioritaire",
      "Garantie satisfaction",
    ],
  },
]

// Codes promo
const promoCodes = [
  { code: "WELCOME20", discount: 20, type: "percentage", description: "20% de réduction pour les nouveaux clients" },
  { code: "STUDENT50", discount: 50, type: "fixed", description: "50€ de réduction" },
  { code: "KENYA2024", discount: 15, type: "percentage", description: "15% de réduction spéciale" },
]

function Cart({ onNavigate }) {
  const [cartItems, setCartItems] = useState([
    // {
    //   id: "consultation",
    //   name: "Guide Premium",
    //   price: 10,
    //   originalPrice: 15,
    //   quantity: 1,
    //   category: "Conseil",
    //   duration: "1 heure",
    // },
    // {
    //   id: "visa-assistance",
    //   name: "Guide Premium Plus",
    //   price: 20,
    //   originalPrice: 30,
    //   quantity: 1,
    //   category: "Visa",
    //   duration: "2-3 semaines",
    // },
  ])

  const [promoCode, setPromoCode] = useState("")
  const [appliedPromo, setAppliedPromo] = useState(null)
  const [showPromoInput, setShowPromoInput] = useState(false)
  const [showRecommendations, setShowRecommendations] = useState(true)

  // Calculs du panier
  const subtotal = cartItems.reduce((sum, item) => sum + item.price * item.quantity, 0)
  const originalTotal = cartItems.reduce((sum, item) => sum + (item.originalPrice || item.price) * item.quantity, 0)
  const savings = originalTotal - subtotal

  let promoDiscount = 0
  if (appliedPromo) {
    if (appliedPromo.type === "percentage") {
      promoDiscount = (subtotal * appliedPromo.discount) / 100
    } else {
      promoDiscount = appliedPromo.discount
    }
  }

  const total = Math.max(0, subtotal - promoDiscount)

  // Fonctions de gestion du panier
  const updateQuantity = (id, newQuantity) => {
    if (newQuantity <= 0) {
      removeItem(id)
      return
    }
    setCartItems(cartItems.map((item) => (item.id === id ? { ...item, quantity: newQuantity } : item)))
  }

  const removeItem = (id) => {
    setCartItems(cartItems.filter((item) => item.id !== id))
  }

  const addToCart = (service) => {
    const existingItem = cartItems.find((item) => item.id === service.id)
    if (existingItem) {
      updateQuantity(service.id, existingItem.quantity + 1)
    } else {
      setCartItems([
        ...cartItems,
        {
          id: service.id,
          name: service.name,
          price: service.price,
          originalPrice: service.originalPrice,
          quantity: 1,
          category: service.category,
          duration: service.duration,
        },
      ])
    }
  }

  const applyPromoCode = () => {
    const promo = promoCodes.find((p) => p.code.toLowerCase() === promoCode.toLowerCase())
    if (promo) {
      setAppliedPromo(promo)
      setShowPromoInput(false)
      setPromoCode("")
    } else {
      alert("Code promo invalide")
    }
  }

  const removePromoCode = () => {
    setAppliedPromo(null)
  }

  // const clearCart = () => {
  //   setCartItems([])
  //   setAppliedPromo(null)
  // }

  // Services recommandés (non présents dans le panier)
  const recommendedServices = availableServices.filter((service) => !cartItems.some((item) => item.id === service.id))

  return (
    <div className="min-h-screen dark:bg-gray-900 bg-gray-50">
      {/* Header */}
    
      
    

      <div className="max-w-7xl  mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {cartItems.length === 0 ? (
          // Panier vide
          <div className="text-center py-16">
            <ShoppingCart className="h-24 w-24 text-green-600 mx-auto mb-6" />
            <h2 className="text-2xl dark:text-gray-200 font-bold text-gray-900 mb-4">Votre panier est vide</h2>
            <p className="text-gray-600 dark:text-gray-400 mb-8 max-w-md mx-auto">
              Découvrez nos services d'accompagnement pour réussir vos études au Kenya
            </p>
            <div className="space-y-4 ">
              <a href="/mksp/guide" className="inline-flex bgC justify-center items-center  py-3 px-5  font-medium text-center text-gray-100 rounded-lg border  border-gray-300 hover:bg-green-500 focus:ring-4 focus:ring-gray-100 dark:text-white dark:border-gray-700 dark:hover:bg-green-500 dark:focus:ring-gray-800" >
                Ajouter un abonement premium
              </a>
              
            </div>
          </div>
        ) : (
          // Panier avec articles
          <div className="grid mt-20 grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Articles du panier */}
            <div className="lg:col-span-2 space-y-6">
              {/* En-tête du panier */}
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold dark:text-gray-100 text-gray-900">Articles sélectionnés</h2>
                <div className="flex items-center space-x-2 text-sm dark:text-gray-300 text-gray-600">
                  <Shield className="h-4 w-4" />
                  <span>Paiement sécurisé</span>
                </div>
              </div>

              {/* Liste des articles */}
              <div className="space-y-4">
                {cartItems.map((item) => (
                  <Card key={item.id} className="p-6 dark:bg-gray-800 dark:border-gray-700 border ">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-2">
                          <h3 className="font-semibold dark:text-green-500 text-lg">{item.name}</h3>
                          <Badge variant="secondary">{item.category}</Badge>
                        </div>
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-4">
                          <div className="flex items-center">
                            <Clock className="h-4 w-4 mr-1" />
                            {item.duration}
                          </div>
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="flex items-center space-x-2">
                            <span className="text-xl font-bold text-green-600">{item.price}€</span>
                            {item.originalPrice && item.originalPrice !== item.price && (
                              <span className="text-sm text-gray-500 line-through">{item.originalPrice}€</span>
                            )}
                          </div>
                          {item.originalPrice && item.originalPrice !== item.price && (
                            <Badge variant="success">
                              -{Math.round(((item.originalPrice - item.price) / item.originalPrice) * 100)}%
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        {/* Contrôles de quantité */}
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="h-8 w-8 p-0"
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{item.quantity}</span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="h-8 w-8 p-0"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                        {/* Bouton supprimer */}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeItem(item.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>

              {/* Services recommandés */}
              {showRecommendations && recommendedServices.length > 0 && (
                <div className="mt-8">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">Services recommandés</h3>
                    <Button variant="ghost" onClick={() => setShowRecommendations(false)} className="p-1">
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {recommendedServices.slice(0, 2).map((service) => (
                      <Card key={service.id} className="p-4 border-dashed border-2 border-gray-200">
                        <div className="flex justify-between items-start mb-2">
                          <h4 className="font-medium">{service.name}</h4>
                          {service.popular && <Badge variant="success">Populaire</Badge>}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">{service.description}</p>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <span className="font-bold text-green-600">{service.price}€</span>
                            {service.originalPrice && (
                              <span className="text-sm text-gray-500 line-through">{service.originalPrice}€</span>
                            )}
                          </div>
                          <Button variant="outline" size="sm" onClick={() => addToCart(service)}>
                            <Plus className="h-4 w-4 mr-1" />
                            Ajouter
                          </Button>
                        </div>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Résumé de commande */}
            <div className="space-y-6">
              {/* Résumé des prix */}
              <Card className="p-6 sticky top-24">
                <h3 className="text-lg font-semibold mb-4">Résumé de la commande</h3>

                <div className="space-y-3 mb-4">
                  <div className="flex justify-between">
                    <span>Sous-total</span>
                    <span>{subtotal.toFixed(2)}€</span>
                  </div>
                  {savings > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Économies</span>
                      <span>-{savings.toFixed(2)}€</span>
                    </div>
                  )}
                  {appliedPromo && (
                    <div className="flex justify-between text-green-600">
                      <div className="flex items-center">
                        <span>Code promo ({appliedPromo.code})</span>
                        <Button variant="ghost" onClick={removePromoCode} className="p-1 ml-2">
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      <span>-{promoDiscount.toFixed(2)}€</span>
                    </div>
                  )}
                </div>

                {/* Code promo */}
                <div className="mb-4">
                  {!showPromoInput && !appliedPromo ? (
                    <Button variant="ghost" onClick={() => setShowPromoInput(true)} className="w-full justify-start">
                      <Gift className="h-4 w-4 mr-2" />
                      Ajouter un code promo
                    </Button>
                  ) : (
                    !appliedPromo && (
                      <div className="space-y-2">
                        <div className="flex space-x-2">
                          <Input
                            placeholder="Code promo"
                            value={promoCode}
                            onChange={(e) => setPromoCode(e.target.value)}
                            onKeyPress={(e) => e.key === "Enter" && applyPromoCode()}
                          />
                          <Button variant="outline" onClick={applyPromoCode}>
                            Appliquer
                          </Button>
                        </div>
                        <Button variant="ghost" onClick={() => setShowPromoInput(false)} className="text-xs">
                          Annuler
                        </Button>
                      </div>
                    )
                  )}
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between text-lg font-semibold">
                    <span>Total</span>
                    <span>{total.toFixed(2)}€</span>
                  </div>
                </div>

                <Button variant="primary" className="w-full mt-6">
                  <CreditCard className="h-5 w-5 mr-2" />
                  Procéder au paiement
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Button>

                {/* Garanties */}
                <div className="mt-6 space-y-3 text-sm text-gray-600">
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span>Paiement 100% sécurisé</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span>Garantie satisfaction 30 jours</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="h-4 w-4 text-green-600 mr-2" />
                    <span>Support client 7j/7</span>
                  </div>
                </div>
              </Card>

              {/* Codes promo disponibles */}
              {!appliedPromo && (
                <Card className="p-6">
                  <h4 className="font-semibold mb-3 flex items-center">
                    <Percent className="h-4 w-4 mr-2 text-green-600" />
                    Codes promo disponibles
                  </h4>
                  <div className="space-y-2">
                    {promoCodes.map((promo) => (
                      <div
                        key={promo.code}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded cursor-pointer hover:bg-gray-100"
                        onClick={() => {
                          setPromoCode(promo.code)
                          setShowPromoInput(true)
                        }}
                      >
                        <div>
                          <div className="font-medium text-sm">{promo.code}</div>
                          <div className="text-xs text-gray-600">{promo.description}</div>
                        </div>
                        <Badge variant="success">
                          {promo.type === "percentage" ? `${promo.discount}%` : `${promo.discount}€`}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              )}

              {/* Informations de livraison */}
              {/* <Card className="p-6">
                <h4 className="font-semibold mb-3">Informations importantes</h4>
                <div className="space-y-3 text-sm text-gray-600">
                  <div className="flex items-start">
                    <AlertCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                    <span>Les consultations se font par vidéoconférence</span>
                  </div>
                  <div className="flex items-start">
                    <AlertCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                    <span>Vous recevrez un email de confirmation après paiement</span>
                  </div>
                  <div className="flex items-start">
                    <AlertCircle className="h-4 w-4 text-blue-600 mr-2 mt-0.5" />
                    <span>Les services démarrent sous 24-48h après validation</span>
                  </div>
                </div>
              </Card> */}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Cart
